import { useEffect, useState } from 'react';
import { css } from '@linaria/core';
import { withSearch } from '@elastic/react-search-ui';
import { useLayoutMode } from './Layout';
import { useMapState } from '../contexts/MapStateContext';

const wrapper = css`
  font-size: 20px;
  line-height: 32px;
  font-weight: 600;

  @media (min-width: 800px) {
    font-size: 22px;
    line-height: 34px;
  }
`;

const searchText = css`
  display: inline-block;
`;

const quotedText = css`
  display: inline-flex;
  align-items: baseline;
  max-width: 100%;

  &::before {
    content: '"';
    flex: 0 0 auto;
  }

  &::after {
    content: '"';
    flex: 0 0 auto;
  }

  &[data-has-location-distance='true'] {
    &::after {
      content: '",';
    }
  }

  &[data-has-location-distance='false'] {
    &::after {
      content: '"';
    }
  }
`;

const searchTermText = css`
  min-width: 0;
  max-width: 24ch;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;

  @media (min-width: 500px) {
    max-width: 35ch;
  }

  @media (min-width: 800px) {
    max-width: 32ch;
  }

  @media (min-width: 1080px) {
    max-width: 24ch;
  }

  @media (min-width: 1400px) {
    max-width: 40ch;
  }
`;

function formatCount(value) {
  return value.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

function PageInfoRealisticCount({
  filters,
  pagingStart,
  pagingEnd,
  totalResults,
  resultSearchTerm,
}) {
  const { layoutMode } = useLayoutMode();
  const { mapState } = useMapState();
  const isMapMode = layoutMode === 'map';
  const incomingMapCount = mapState?.totalMapRecords;

  const [displayedVisibleCount, setDisplayedVisibleCount] = useState(null);

  useEffect(() => {
    if (!isMapMode) {
      setDisplayedVisibleCount(null);
      return;
    }
    if (incomingMapCount != null) {
      setDisplayedVisibleCount(incomingMapCount);
    }
  }, [isMapMode, incomingMapCount]);

  const resultsLabel = totalResults === 1 ? 'result' : 'results';

  const location = filters?.find(({ field }) => field === 'location')
    ?.values?.[0];

  const locationDistance = filters
    ?.find(({ field }) => field === 'locationDistance')
    ?.values?.[0]?.replace('mi', '');

  const searchLocationFragment = location && (
    <>
      <span> in </span>
      <span
        className={quotedText}
        data-has-location-distance={locationDistance > 0}
      >
        <strong className={searchTermText} title={location}>
          {location}
        </strong>
      </span>
    </>
  );

  const searchLocationDistanceFragment = locationDistance &&
    locationDistance > 0 && (
      <>
        <span title={locationDistance}>
          {' '}
          within {`${locationDistance}`} miles
        </span>
      </>
    );

  const searchTermFragment = resultSearchTerm && (
    <div className={searchText}>
      <span>for </span>
      <span className={quotedText}>
        <strong className={searchTermText} title={resultSearchTerm}>
          {resultSearchTerm}
        </strong>
      </span>
    </div>
  );

  if (isMapMode) {
    if (displayedVisibleCount == null) {
      return (
        <div aria-atomic="true" aria-live="polite" className={wrapper}>
          <strong>{formatCount(totalResults)}</strong> {resultsLabel}
        </div>
      );
    }

    return (
      <div aria-atomic="true" aria-live="polite" className={wrapper}>
        Showing <strong>{formatCount(displayedVisibleCount)}</strong> out of{' '}
        <strong>{formatCount(totalResults)}</strong> {resultsLabel}{' '}
        {searchTermFragment}
        {searchLocationFragment} {searchLocationDistanceFragment}
      </div>
    );
  }

  return (
    <div aria-atomic="true" aria-live="polite" className={wrapper}>
      Showing{' '}
      <strong>
        {pagingStart} - {pagingEnd}
      </strong>{' '}
      out of <strong>{formatCount(totalResults)}</strong> {resultsLabel}{' '}
      {searchTermFragment} {searchLocationFragment}{' '}
      {searchLocationDistanceFragment}
    </div>
  );
}

export default withSearch(
  ({
    filters,
    pagingStart,
    pagingEnd,
    resultsPerPage,
    totalResults,
    resultSearchTerm,
  }) => ({
    filters,
    pagingStart,
    pagingEnd,
    resultsPerPage,
    totalResults,
    resultSearchTerm,
  }),
)(PageInfoRealisticCount);
